import * as FullStory from '@fullstory/browser';
import { reaction } from 'mobx';
import { flow } from 'mobx-state-tree';

import { logger } from '@lusha/client-logger';
import { startIdleLogout } from '@lusha/idle-management';

import { addUserToFreeTrial, fetchUserFreeTrialEligibility } from '../../../API/freeTrialAPI';
import { getAccountIntegrations } from '../../../API/integrationsAPI';
import { fetchUserRole } from '../../../API/userAPI';
import { isLushaLists } from '../../../components/SaveContact/constants';
import { dismissNotifications } from '../../../hooks/useNotification';
import FEATURES from '../features';

const Actions = self => ({
  afterCreate: () => {
    reaction(
      () => self.isLoaded,
      () => {
        if (self.isLoaded) {
          if (!self.roleId) {
            self.loadUserRole();
          }

          self.identifyFullstoryUser();
          if (self.isAccountAdmin && self.billing?.isPaid) {
            self.company.loadInfo();
          }

          if (self.idleTimeout.isEnabled && !self.idleTimeout.scriptAlreadyLoaded) {
            startIdleLogout({ idleTime: self.idleTimeout.time });
            self.idleTimeout.setScriptAlreadyLoaded(true);
          }

          self.fetchAndUpdateAccountIntegrations();
        }
      }
    );
  },
  load: user => {
    try {
      dismissNotifications();
      self.setUser(user);
      self.loadFreeTrial(user.freeTrial);
      self.isLoaded = true;
    } catch (e) {
      self.id = undefined;
    }
  },
  identifyFullstoryUser: () => {
    try {
      FullStory.identify(self.id.toString(), {
        displayName: `${self.firstName} ${self.lastName}`,
        email: self.email,
      });
    } catch (error) {
      logger.warn('Failed to identify FullStory user', error);
    }
  },
  loadUserRole: flow(function* loadUserRole() {
    if (self.isAccountSuspended) {
      return;
    }

    try {
      const response = yield fetchUserRole();
      self.setRoleId(response.role?.id);
    } catch (error) {
      self.setRoleId(null);
    }
  }),

  startFreeTrial: flow(function* startFreeTrial({ features, tab, trigger }) {
    let hasResult;

    try {
      const freeTrial = yield addUserToFreeTrial({
        features, source: 'plugin', tab, trigger,
      });
      self.setFreeTrial(freeTrial);

      hasResult = true;
    } catch (e) {
      hasResult = false;
    }

    return hasResult;
  }),

  loadFreeTrial: flow(function* loadFreeTrial(freeTrial) {
    if (self.isAccountSuspended) {
      return;
    }

    try {
      const { eligibleForFreeTrial } = yield fetchUserFreeTrialEligibility();
      self.setFreeTrial(freeTrial, eligibleForFreeTrial);
    } catch (e) {
      self.setFreeTrial(null);
    }
  }),

  setIsAccountIntegrationLoaded: setter => {
    self.isAccountIntegrationLoaded = setter;
  },

  setIsFirstShow: setter => {
    self.isFirstShow = setter;
  },
  setIsFirstOutOfCredits: setter => {
    self.isFirstOutOfCredits = setter;
  },

  setUser: user => {
    self.id = user.id;
    self.accountId = user.accountId;
    self.firstName = user.firstName;
    self.lastName = user.lastName;
    self.countryCode = user.countryCode;
    self.email = user.email;
    self.phone = user.phone?.toString();
    self.hashedEmail = user.hashedEmail;
    self.industry = user.industry;
    self.isFirstShow = user.analytics ? !user.analytics.firstShowDate : false;
    self.isFirstOutOfCredits = user.analytics ? !user.analytics.firstOutOfCredits : false;
    self.isAccountAdmin = Boolean(user.admin);
    self.accountDomain = user.domain;
    self.groupName = user.group?.name;
    self.features = user.features ? [...user.features] : [];
    self.isSearchByDataPointEnabled = (user.features || []).includes(FEATURES.SHOW_SPECIFIC_DATA_POINTS) && !(user.features || []).includes(FEATURES.SCALE_FREE_TRIAL);
    self.isOneWalletEnabled = (user.features || []).includes(FEATURES.ONE_WALLET);
    self.priceBook = user.accountBillingPlans?.[0]?.priceBook;
    self.accountUnfreezeDate = user.accountBillingPlans?.[0]?.endDate || '';
    self.roleId = user?.role?.id;
    self.idleTimeout.setIdleTimeout(user.accountSettings?.idleLogoutTimeMinutes || null);
    self.subscriptionStatus = user.accountBillingPlans?.[0]?.subscriptionStatus || '';

    if (user?.account) {
      self.accountStatus = user.account.status || 'active';
      self.accountStatusReason = user.account.statusReason || '';
    }

    self.setBillingInfo(user.billing, user.accountBillingPlans);
    self.analytics.setInfo(user.analytics);

    self.initUBP(user.iubp);
  },

  setRoleId: roleId => {
    self.roleId = roleId;
  },

  isIntegrationConnected: integration => isLushaLists(integration) || (self.activeIntegrations.includes(integration) ?? false),

  updateIntegration: (integrationKey, status) => {
    if (status) {
      self.activeIntegrations.push(integrationKey);
    } else {
      self.activeIntegrations = self.activeIntegrations.filter(item => item !== integrationKey);
    }
  },

  setBillingInfo: (billing, accountBillingPlans) => {
    if (!billing) {
      return;
    }

    const { category, isPaid, endDate, renewalType, renews = true } = billing;

    const stripeInvoiceLink = accountBillingPlans?.[0]?.stripeInvoiceLink;
    const stripePaymentMethodCountryCode = accountBillingPlans?.[0]?.stripePaymentMethodCountryCode;
    const billingPlanId = accountBillingPlans?.[0]?.billingPlanId;
    const startDate = accountBillingPlans?.[0]?.startDate;

    self.billing = {
      category,
      isPaid,
      endDate: new Date(endDate),
      stripeInvoiceLink,
      stripePaymentMethodCountryCode,
      renewalType,
      billingPlanId,
      renews,
      startDate: new Date(startDate),
    };
  },

  setFreeTrial: (freeTrial = {}, eligibleForFreeTrial = false) => {
    if (!freeTrial) {
      self.freeTrial = {
        eligibleForFreeTrial,
      };

      return;
    }

    const { startDate, endDate, isExpired, isStarted, source } = freeTrial;

    self.freeTrial = {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      isExpired: Boolean(isExpired),
      isStarted: Boolean(isStarted),
      eligibleForFreeTrial,
      source,
    };

    if (self.freeTrial.isActive) {
      self.features = [...new Set([...self.features, ...freeTrial.features])];
    }
  },

  setIntegrations: accountIntegrations => {
    if (accountIntegrations) {
      self.activeIntegrations = accountIntegrations.map(item => item.integration.name);
    }
  },

  onCreditUsed: () => {
    --self.availableCredits;
  },

  fetchAndUpdateAccountIntegrations: async () => {
    if (!self.hasIntegrationPermissions) {
      return;
    }
    try {
      const accountIntegrations = await getAccountIntegrations();
      self.setIntegrations(accountIntegrations);
      self.setIsAccountIntegrationLoaded(true);
    } catch (error) {
      logger.error(`Failed to fetch account integrations: ${JSON.stringify(error)}`, { accountId: self?.accountId, userId: self?.id, error });
    }
  },

  initUBP: ubp => {
    if (ubp?.extensionBulkShows && ubp.extensionBulkShows.availableCredits !== 'unlimited') {
      self.ubp = {
        bulkShowCredits: ubp.extensionBulkShows.availableCredits,
        totalBulkShowCredits: ubp.extensionBulkShows.totalCredits,
      };
    }
  },

  setAccountIntentTopicsCount(value) {
    self.accountIntentTopicsCount = value;
  },

  setBulkAvailableCredits(value) {
    self.bulkAvailableCredits = value ?? 0;
  },

  setAccountIntentTopicsLoaded(value) {
    self.accountIntentTopicsLoaded = value;
  },

});

export default Actions;
